import React from 'react';
import {
  CapaNotificationRfq,
  CapaNotificationsOutput,
  CapaNotificationType
} from '@generated/graphql';
import EmptyScreen from './EmptyScreenPresenter';
import NotificationPopperHeader from './NotificationPopperHeader';
import { Grid, Popper, Backdrop } from '@material-ui/core';
import { useStyles } from '../useStyles';
import { Card, getNotificationContent, refundTypes, validTypes } from '../Card';
import { annotationTimeFormatter, formatCurrency } from '@utils/formatter';
import { ChevronRight } from '@carbon/icons-react';
import { useHistory } from 'react-router-dom';

interface Props {
  data?: CapaNotificationsOutput;
  open: boolean;
  anchorEl?: HTMLButtonElement | null;
  onClose: () => void;
  onReadNotification: (seq: number) => void;
  onReadAllNotification: () => void;
}

const NotificationPopperContainer: React.FC<Props> = ({
  data,
  open,
  anchorEl,
  onClose,
  onReadNotification,
  onReadAllNotification
}) => {
  const history = useHistory();
  const classes = useStyles();
  const notificationList =
    data?.data?.notifications.filter((noti) =>
      validTypes.includes(noti.data?.notificationType as CapaNotificationType)
    ) ?? [];

  return (
    <React.Fragment>
      <Backdrop
        className={classes.backDrop}
        open={open}
        invisible
        onClick={onClose}
      />
      <Popper
        className={classes.popoverRoot}
        open={open}
        anchorEl={anchorEl}
        disablePortal
      >
        <Grid container className={classes.root}>
          <NotificationPopperHeader
            onReadAllNotification={onReadAllNotification}
          />
          <Grid container className={classes.content} wrap="nowrap">
            {notificationList.length < 1 ? (
              <EmptyScreen />
            ) : (
              notificationList.map((data) => {
                const notificationType = data.data?.notificationType;
                let notification: CapaNotificationRfq | null = null;

                if (!notificationType) return null;

                if (data.data?.__typename === 'CAPANotificationRfq') {
                  notification = data.data;
                }

                if (!notification) return null;

                const notiContent = getNotificationContent(data);

                if (!notiContent) return null;

                const { color, title, content } = notiContent;

                const onClickCard = () => {
                  onReadNotification(data.seq);
                  const chatRoomId = data?.data?.chatRoomId;
                  if (chatRoomId) {
                    history.push(`/chat/${chatRoomId}`);
                  }
                };

                return (
                  <Card key={data?.seq} onClick={onClickCard}>
                    <Card.Header
                      color={color}
                      createdAt={annotationTimeFormatter(data.createdAt)}
                    >
                      진행알림
                    </Card.Header>
                    <Card.Text
                      variant="body1"
                      fontWeight="500"
                      gutterBottom={4}
                    >
                      {title}
                    </Card.Text>

                    <Card.Text
                      variant="body2"
                      color="gray700"
                      fontWeight="400"
                      gutterBottom={8}
                    >
                      {content}
                    </Card.Text>

                    <Card.Text variant="body2">
                      프로젝트명&nbsp;:&nbsp;
                      {notification?.__typename === 'CAPANotificationRfq' &&
                        notification.projectName}
                    </Card.Text>

                    {refundTypes.includes(notificationType) && (
                      <Card.Text variant="body2">
                        환불금액&nbsp;:&nbsp;
                        {data.data?.__typename === 'CAPANotificationRfq' &&
                          formatCurrency(notification.refundAmt ?? 0)}
                        원
                      </Card.Text>
                    )}

                    {notificationType ===
                      CapaNotificationType.PurchaseConfirmed && (
                      <Card.Text variant="body2">
                        정산금액&nbsp;:&nbsp;
                        {data.data?.__typename === 'CAPANotificationRfq' &&
                          formatCurrency(notification.depositAmt ?? 0)}
                        원
                      </Card.Text>
                    )}

                    <Card.Text
                      color="gray700"
                      variant="subtitle2"
                      className={classes.cardCTA}
                    >
                      채팅방으로 이동 <ChevronRight />
                    </Card.Text>
                  </Card>
                );
              })
            )}
          </Grid>
        </Grid>
      </Popper>
    </React.Fragment>
  );
};

export default NotificationPopperContainer;
